import React, { useEffect, useState } from 'react';
import { Database, Pencil, Trash2, ChevronDown, Inbox } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Card, Button, Input, LoadingState, EmptyState, ConfirmDialog, Table, THead, TBody, TR, TH, TD } from '../../components/ui';

const DatabaseExplorer = () => {
  const [dbInfo, setDbInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [collectionData, setCollectionData] = useState(null);
  const [collectionLoading, setCollectionLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);

  // Edit states
  const [editingRow, setEditingRow] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [updateLoading, setUpdateLoading] = useState(false);

  // Dialog states (replace window.confirm / window.prompt)
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pendingVerification, setPendingVerification] = useState(null);

  useEffect(() => {
    api.get('/super-admin/database/info')
      .then(res => setDbInfo(res.data.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const loadCollection = (colName, pg = 1, srch = '') => {
    setCollectionLoading(true);
    setSelectedCollection(colName);
    setPage(pg);
    setSearch(srch);
    setEditingRow(null);
    const params = new URLSearchParams({ page: pg, limit: 20 });
    if (srch) params.set('search', srch);
    api.get(`/super-admin/database/${colName}?${params.toString()}`)
      .then(res => setCollectionData(res.data))
      .catch(console.error)
      .finally(() => setCollectionLoading(false));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadCollection(selectedCollection, 1, searchInput);
  };

  const performDelete = async (docId) => {
    try {
      await api.delete(`/super-admin/database/${selectedCollection}/${docId}`);
      toast.success('Record permanently deleted');
      loadCollection(selectedCollection, page, search);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete record');
    } finally {
      setDeleteTarget(null);
    }
  };

  const startEditing = (doc, idx) => {
    setEditingRow(idx);
    setEditValues({ ...doc });
    setExpandedRow(null);
  };

  const cancelEditing = () => {
    setEditingRow(null);
    setEditValues({});
  };

  const performUpdate = async (docId, verificationKey = '') => {
    setUpdateLoading(true);
    try {
      await api.put(`/super-admin/database/${selectedCollection}/${docId}`, {
        ...editValues,
        verificationKey,
      });

      toast.success('Record updated successfully');
      setEditingRow(null);
      setPendingVerification(null);
      loadCollection(selectedCollection, page, search);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update record');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleUpdate = (docId) => {
    if (selectedCollection === 'users') {
      const original = collectionData.data.find(d => d._id === docId);
      if (original.role === 'SUPER_ADMIN' || editValues.role === 'SUPER_ADMIN' || editValues.password) {
        setPendingVerification({ docId });
        return;
      }
    }
    performUpdate(docId, '');
  };

  const renderValue = (val) => {
    if (val === null || val === undefined) return <span className="text-slate-600 italic">null</span>;
    if (typeof val === 'boolean') return <span className={val ? 'text-green-400' : 'text-red-400'}>{String(val)}</span>;
    if (typeof val === 'number') return <span className="text-yellow-400">{val}</span>;
    if (typeof val === 'object') {
      if (val instanceof Date || (typeof val === 'string' && val.match(/^\d{4}-/))) {
        return <span className="text-blue-400">{new Date(val).toLocaleString()}</span>;
      }
      return <span className="text-slate-500 text-xs">[Object]</span>;
    }
    const str = String(val);
    if (str.length > 60) return <span className="text-slate-300 text-xs">{str.slice(0, 60)}…</span>;
    return <span className="text-slate-300">{str}</span>;
  };

  const renderEditField = (docId, field, value) => {
    const isProtected = ['_id', 'createdAt', 'updatedAt', '__v'].includes(field);
    if (isProtected) return <span className="text-slate-600 font-mono">{String(value)}</span>;

    if (typeof value === 'boolean') {
      return (
        <select
          value={String(value)}
          onChange={e => setEditValues({ ...editValues, [field]: e.target.value === 'true' })}
          className="bg-slate-800 border border-slate-700 text-white text-xs rounded px-1.5 py-0.5 focus:outline-none focus:border-violet-500"
        >
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      );
    }

    if (field === 'role' && selectedCollection === 'users') {
      return (
        <select
          value={value}
          onChange={e => setEditValues({ ...editValues, [field]: e.target.value })}
          className="bg-slate-800 border border-slate-700 text-white text-xs rounded px-1.5 py-0.5 focus:outline-none focus:border-violet-500"
        >
          <option value="STUDENT">STUDENT</option>
          <option value="PROCTOR">PROCTOR</option>
          <option value="ADMIN">ADMIN</option>
          <option value="SUPER_ADMIN">SUPER_ADMIN</option>
        </select>
      );
    }

    return (
      <div className="flex flex-col gap-1">
        <input
          type="text"
          value={value || ''}
          onChange={e => setEditValues({ ...editValues, [field]: e.target.value })}
          className="bg-slate-800 border border-slate-700 text-white text-xs rounded px-1.5 py-0.5 focus:outline-none focus:border-violet-500 w-full"
        />
        {field === 'username' && selectedCollection === 'users' && (
          <input
            type="password"
            placeholder="Set new password..."
            onChange={e => setEditValues({ ...editValues, password: e.target.value })}
            className="bg-slate-950 border border-red-900/40 text-white text-[10px] rounded px-1.5 py-0.5 focus:outline-none focus:border-red-700 placeholder-slate-700 w-full mt-1"
          />
        )}
      </div>
    );
  };

  if (loading) return <LoadingState tone="dark" label="Loading database info…" />;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold text-white">Database Explorer</h2>
        <p className="text-slate-500 text-sm mt-1">Inspect and edit all collections in real-time</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Left panel: DB info + collections */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-gradient-to-br from-violet-900/40 to-violet-800/20 border border-violet-700/30 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Database className="w-5 h-5 text-violet-400" />
              <span className="text-white font-semibold text-sm">Database</span>
            </div>
            <p className="text-violet-300 font-mono text-lg font-bold">{dbInfo?.databaseName}</p>
            <p className="text-slate-500 text-xs mt-1">{dbInfo?.collections?.length} collections</p>
          </div>

          <Card tone="dark" padding="p-0" className="overflow-hidden">
            <div className="px-4 py-2 border-b border-slate-800">
              <p className="text-slate-400 text-xs font-medium uppercase tracking-wider">Collections</p>
            </div>
            <div className="divide-y divide-slate-800/60 overflow-y-auto max-h-[500px]">
              {(dbInfo?.collections || []).map(col => (
                <button
                  key={col.name}
                  onClick={() => loadCollection(col.name, 1, '')}
                  className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors hover:bg-slate-800/60 ${
                    selectedCollection === col.name ? 'bg-violet-900/30 border-l-2 border-violet-500' : ''
                  }`}
                >
                  <div>
                    <p className={`text-sm font-medium ${selectedCollection === col.name ? 'text-violet-300' : 'text-slate-300'}`}>
                      {col.name}
                    </p>
                    <p className="text-xs text-slate-600">{col.fields.slice(0, 3).join(', ')}{col.fields.length > 3 ? '…' : ''}</p>
                  </div>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                    selectedCollection === col.name ? 'bg-violet-700/50 text-violet-300' : 'bg-slate-800 text-slate-500'
                  }`}>
                    {col.count}
                  </span>
                </button>
              ))}
            </div>
          </Card>
        </div>

        {/* Right panel: Collection data */}
        <div className="lg:col-span-3">
          {!selectedCollection ? (
            <Card tone="dark" className="flex items-center justify-center h-64">
              <EmptyState tone="dark" icon={Database} title="Select a collection" description="Choose a collection on the left to explore its documents." />
            </Card>
          ) : (
            <Card tone="dark" padding="p-0" className="overflow-hidden flex flex-col h-full min-h-[600px]">
              {/* Collection header */}
              <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-white font-mono font-semibold">{selectedCollection}</span>
                  {collectionData && (
                    <span className="text-xs text-slate-500">{collectionData.pagination?.total} docs</span>
                  )}
                </div>
                <form onSubmit={handleSearch} className="flex gap-2">
                  <Input
                    tone="dark"
                    className="w-48 text-xs"
                    placeholder="Search…"
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                  />
                  <Button type="submit" tone="dark" variant="primary" size="sm">Search</Button>
                </form>
              </div>

              {collectionLoading ? (
                <LoadingState tone="dark" label="Loading documents…" className="flex-1" />
              ) : collectionData ? (
                <>
                  <div className="flex-1 overflow-auto">
                    {collectionData.data?.length > 0 ? (
                      <Table tone="dark" className="text-xs">
                        <THead>
                          <TR>
                            <TH className="w-20">Actions</TH>
                            {collectionData.fields?.map(f => (
                              <TH key={f}>{f}</TH>
                            ))}
                          </TR>
                        </THead>
                        <TBody>
                          {collectionData.data.map((doc, idx) => (
                            <React.Fragment key={doc._id || idx}>
                              <TR
                                className={editingRow === idx ? 'bg-violet-900/20' : ''}
                                onClick={() => editingRow === null && setExpandedRow(expandedRow === idx ? null : idx)}
                              >
                                <TD className="whitespace-nowrap" onClick={e => e.stopPropagation()}>
                                  {editingRow === idx ? (
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => handleUpdate(doc._id)}
                                        disabled={updateLoading}
                                        className="text-emerald-500 hover:text-emerald-400 font-bold"
                                        title="Save changes"
                                      >
                                        Save
                                      </button>
                                      <button
                                        onClick={cancelEditing}
                                        className="text-slate-500 hover:text-slate-400"
                                        title="Cancel editing"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="flex gap-2 items-center">
                                      <button
                                        onClick={() => startEditing(doc, idx)}
                                        className="text-blue-500 hover:text-blue-400 transition-colors"
                                        title="Edit this record"
                                      >
                                        <Pencil className="w-3.5 h-3.5" />
                                      </button>
                                      <button
                                        onClick={() => setDeleteTarget(doc._id)}
                                        className="text-red-500 hover:text-red-400 transition-colors"
                                        title="Permanently delete"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                      <ChevronDown className={`w-3 h-3 text-slate-700 transition-transform duration-200 ${expandedRow === idx ? 'rotate-180' : ''}`} />
                                    </div>
                                  )}
                                </TD>
                                {collectionData.fields?.map(f => (
                                  <TD key={f} className="whitespace-nowrap max-w-xs overflow-hidden">
                                    {editingRow === idx
                                      ? renderEditField(doc._id, f, editValues[f])
                                      : renderValue(doc[f])
                                    }
                                  </TD>
                                ))}
                              </TR>
                              {expandedRow === idx && (
                                <TR className="bg-slate-900/80 hover:bg-slate-900/80">
                                  <TD colSpan={collectionData.fields?.length + 1}>
                                    <div className="flex gap-4">
                                      <div className="flex-1">
                                        <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-1 font-bold">Document Payload</p>
                                        <pre className="text-[10px] text-slate-400 bg-slate-950 rounded p-3 overflow-auto max-h-60 font-mono border border-slate-800">
                                          {JSON.stringify(doc, null, 2)}
                                        </pre>
                                      </div>
                                      {selectedCollection === 'users' && (
                                        <div className="w-48">
                                          <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-1 font-bold">Quick Actions</p>
                                          <button
                                            onClick={() => startEditing(doc, idx)}
                                            className="w-full text-left px-3 py-1.5 bg-slate-800 text-slate-300 rounded mb-1 hover:bg-slate-700 transition-colors"
                                          >
                                            Edit All Fields
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </TD>
                                </TR>
                              )}
                            </React.Fragment>
                          ))}
                        </TBody>
                      </Table>
                    ) : (
                      <EmptyState tone="dark" icon={Inbox} title="No documents" description="No documents found in this collection." />
                    )}
                  </div>

                  {/* Pagination */}
                  {collectionData.pagination?.pages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800 sticky bottom-0 bg-slate-900">
                      <p className="text-slate-600 text-[10px] font-mono">
                        PAGE {collectionData.pagination.page} / {collectionData.pagination.pages}
                      </p>
                      <div className="flex gap-1.5">
                        <button
                          onClick={() => loadCollection(selectedCollection, page - 1, search)}
                          disabled={page === 1}
                          className="px-2.5 py-1 bg-slate-800 text-slate-400 rounded text-[10px] font-bold uppercase disabled:opacity-30 hover:bg-slate-700 transition-colors"
                        >
                          Prev
                        </button>
                        <button
                          onClick={() => loadCollection(selectedCollection, page + 1, search)}
                          disabled={page === collectionData.pagination.pages}
                          className="px-2.5 py-1 bg-slate-800 text-slate-400 rounded text-[10px] font-bold uppercase disabled:opacity-30 hover:bg-slate-700 transition-colors"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </Card>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => performDelete(deleteTarget)}
        tone="dark"
        variant="danger"
        title="Permanently delete this record?"
        description="This removes the document from the database immediately. This cannot be undone."
        confirmLabel="Delete"
      />

      <ConfirmDialog
        open={!!pendingVerification}
        onClose={() => setPendingVerification(null)}
        onConfirm={(key) => performUpdate(pendingVerification.docId, key)}
        tone="dark"
        title="Verification required"
        description="This change modifies a sensitive user field (role or password). Enter the Super Admin verification key to continue."
        confirmLabel="Save"
        requireInput
        inputType="password"
        inputLabel="Verification key"
      />
    </div>
  );
};

export default DatabaseExplorer;
